import type { OptimizerAuditV2Record } from '../../types/audit.types';

export type PortfolioAuditTradeSummary = {
  tradeCount: number;
  totalBuyCents: number;
  totalSellCents: number;
  validationErrors: string[];
  positionChangeCount: number;
  topPositionChangesCents: number[];
};

export const buildAuditByReportId = (
  audits: OptimizerAuditV2Record[],
): Map<string, OptimizerAuditV2Record> => {
  const map = new Map<string, OptimizerAuditV2Record>();

  for (const audit of audits) {
    if (audit.portfolioReportId) {
      map.set(audit.portfolioReportId, audit);
    }
  }

  return map;
};

export const hasPortfolioValidationFailure = (
  audit: OptimizerAuditV2Record | undefined,
): boolean => audit?.passed === false;

export const hasPortfolioValidationWarnings = (
  audit: OptimizerAuditV2Record | undefined,
): boolean =>
  Boolean(audit?.validationErrors?.length) && audit?.passed !== false;

export const getPortfolioAuditPassedState = (
  audit: OptimizerAuditV2Record | undefined,
): boolean | undefined => {
  if (!audit) {
    return undefined;
  }

  if (hasPortfolioValidationFailure(audit)) {
    return false;
  }

  if (audit.passed === true) {
    return true;
  }

  if (hasPortfolioValidationWarnings(audit)) {
    return true;
  }

  return undefined;
};

export const summarizePortfolioAudit = (
  audit: OptimizerAuditV2Record | undefined,
): PortfolioAuditTradeSummary | null => {
  if (!audit) {
    return null;
  }

  let tradeCount = 0;
  let totalBuyCents = 0;
  let totalSellCents = 0;

  for (const subAccount of audit.subAccountResults ?? []) {
    tradeCount += subAccount.tradeCount ?? 0;
    totalBuyCents += subAccount.totalBuyCents ?? 0;
    totalSellCents += subAccount.totalSellCents ?? 0;
  }

  const weightDiffs = (audit.subAccountResults ?? [])
    .flatMap((subAccount) => subAccount.weightDiffs ?? [])
    .map((diff) => Math.abs(diff.deltaCents ?? 0))
    .filter((value) => value > 0)
    .sort((a, b) => b - a);

  const positionChangeCount = (audit.subAccountResults ?? []).reduce(
    (count, subAccount) => count + (subAccount.weightDiffs?.length ?? 0),
    0,
  );

  return {
    tradeCount,
    totalBuyCents,
    totalSellCents,
    validationErrors: audit.validationErrors ?? [],
    positionChangeCount,
    topPositionChangesCents: weightDiffs.slice(0, 5),
  };
};
